/**
 * v138 — Aggregate dataset contribution client.
 *
 * Opt-in workflow:
 *   1. User signs in (v136.0) and toggles "Contribute to the community dataset"
 *      in the Range Log settings panel.
 *   2. `setOptIn(true)` posts to /v1/aggregate/optin and the Worker flips
 *      the user's flag in D1.
 *   3. On every subsequent session commit, the client sends ONLY the
 *      aggregated stats (mean radius, group 95%, distance, conditions,
 *      setup signature) — NO photos, NO location, NO labels, NO notes.
 *
 * Anonymous mode and not-opted-in users: every call below is a no-op
 * (or returns false / null). The hook never blocks normal operation.
 */

import type { ShotSession } from '@/range/types';
import type { BuildState, BuildDerived } from '@/types';
import { fetchIdentity } from '@/auth/identityBridge';
import { CLOUD_SYNC_BASE } from './cloudSync';

const HEADER_CUSTOMER_ID = 'X-Sparrow-Customer-Id';
const HEADER_CUSTOMER_EMAIL = 'X-Sparrow-Customer-Email';
const HEADER_CUSTOMER_HASH = 'X-Sparrow-Customer-Hash';
const LOCAL_CONTRIB_COUNT_KEY = 'sparrow.aggregate.contribCount';

/** Shape sent to the Worker — explicitly enumerated allowlist. */
interface AggregateRangeSessionPayload {
  distanceYd: number;
  shotCount: number;
  meanRadiusIn: number;
  group95RadiusIn: number;
  conditions?: {
    temperatureF?: number;
    windMph?: number;
    densityAltitudeFt?: number;
  };
  setupSignature?: {
    fps: number;
    weightGr: number;
    bcIBO: number;
    foc: number;
    spine: number;
    bowType: 'compound' | 'recurve' | 'traditional';
  };
}

async function authHeaders(): Promise<HeadersInit | null> {
  const id = await fetchIdentity();
  if (!id.signedIn || !id.customerId || !id.customerHash) return null;
  return {
    [HEADER_CUSTOMER_ID]: id.customerId,
    [HEADER_CUSTOMER_HASH]: id.customerHash,
    ...(id.customerEmail ? { [HEADER_CUSTOMER_EMAIL]: id.customerEmail } : {}),
  };
}

/** Get current opt-in status from the Worker. */
export async function getOptInStatus(): Promise<{ ok: boolean; contributing: boolean }> {
  const headers = await authHeaders();
  if (!headers) return { ok: false, contributing: false };
  try {
    const res = await fetch(`${CLOUD_SYNC_BASE}/v1/aggregate/status`, { headers });
    if (!res.ok) return { ok: false, contributing: false };
    const json = (await res.json()) as { contributing: boolean };
    return { ok: true, contributing: !!json.contributing };
  } catch {
    return { ok: false, contributing: false };
  }
}

/** Toggle the opt-in flag. */
export async function setOptIn(on: boolean): Promise<boolean> {
  const headers = await authHeaders();
  if (!headers) return false;
  try {
    const res = await fetch(`${CLOUD_SYNC_BASE}/v1/aggregate/optin`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ on }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Construct the payload from a session + the build state at session time.
 * Pure function — testable. Caller guards with the opt-in flag.
 */
export function buildSessionPayload(
  session: ShotSession,
  build?: { state: BuildState; derived: BuildDerived },
): AggregateRangeSessionPayload | null {
  if (typeof session.distanceYd !== 'number') return null;
  const shotCount = session.richShots?.length ?? session.arrowCount ?? 0;
  if (shotCount === 0) return null;

  // Compute mean + 95% group from richShots (canonical inches storage).
  let meanRadiusIn = 0;
  let group95RadiusIn = 0;
  if (session.richShots && session.richShots.length > 0) {
    const shots = session.richShots
      .filter((s) => typeof s.hitXIn === 'number' && typeof s.hitYIn === 'number')
      .map((s) => ({ x: s.hitXIn as number, y: s.hitYIn as number }));
    if (shots.length === 0) return null;
    const radii = shots.map((s) => Math.hypot(s.x, s.y));
    meanRadiusIn = radii.reduce((a, b) => a + b, 0) / radii.length;
    const cx = shots.reduce((a, b) => a + b.x, 0) / shots.length;
    const cy = shots.reduce((a, b) => a + b.y, 0) / shots.length;
    const radiiFromCenter = shots.map((s) => Math.hypot(s.x - cx, s.y - cy));
    const meanFromCenter = radiiFromCenter.reduce((a, b) => a + b, 0) / radiiFromCenter.length;
    const variance =
      radiiFromCenter.reduce((a, b) => a + (b - meanFromCenter) ** 2, 0) /
      Math.max(1, radiiFromCenter.length);
    group95RadiusIn = 2 * Math.sqrt(variance);
  } else {
    // v1 quick-log session — use the stored groupSizeIn as a 95%-equivalent proxy.
    group95RadiusIn = (session.groupSizeIn ?? 0) / 2;
    meanRadiusIn = group95RadiusIn * 0.5;
  }

  const payload: AggregateRangeSessionPayload = {
    distanceYd: session.distanceYd,
    shotCount,
    meanRadiusIn,
    group95RadiusIn,
  };

  if (session.conditions) {
    payload.conditions = {
      temperatureF: session.conditions.temperatureF,
      windMph: session.conditions.windMph,
      densityAltitudeFt: session.conditions.densityAltitudeFt,
    };
  }

  if (build) {
    payload.setupSignature = {
      fps: build.derived.estimatedFps,
      weightGr: build.derived.weight,
      bcIBO: build.derived.bc,
      foc: build.derived.foc,
      spine: build.state.spineKey,
      bowType: build.state.bow.type,
    };
  }

  return payload;
}

/**
 * Post a session-derived event to the Worker. Returns true on success,
 * false on any failure (network, auth, opt-in revoked server-side).
 * Best-effort — caller does not block on this.
 */
export async function contributeSession(
  session: ShotSession,
  build?: { state: BuildState; derived: BuildDerived },
): Promise<boolean> {
  const payload = buildSessionPayload(session, build);
  if (!payload) return false;
  const headers = await authHeaders();
  if (!headers) return false;
  try {
    const res = await fetch(`${CLOUD_SYNC_BASE}/v1/aggregate/contribute`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventId: cryptoRandomId(),
        eventType: 'range_session',
        schemaVersion: 1,
        payload,
      }),
    });
    if (res.ok) {
      bumpLocalContribCount();
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

function cryptoRandomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as Crypto & { randomUUID: () => string }).randomUUID();
  }
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function bumpLocalContribCount(): void {
  try {
    const cur = Number(window.localStorage.getItem(LOCAL_CONTRIB_COUNT_KEY) ?? '0');
    window.localStorage.setItem(LOCAL_CONTRIB_COUNT_KEY, String(cur + 1));
  } catch {
    // ignore
  }
}

export function readLocalContribCount(): number {
  try {
    return Number(window.localStorage.getItem(LOCAL_CONTRIB_COUNT_KEY) ?? '0');
  } catch {
    return 0;
  }
}
